<template>
  <div class="empresas-crear-container p-4 shadow-sm rounded">
    <h4 class="page-title mb-4">{{ isEditMode ? 'Editar Empresa Existente' : 'Registrar Nueva Empresa' }}</h4>

    <div class="card mb-4">
      <div class="card-header fw-bold py-1">Datos de la Empresa</div>
      <div class="card-body py-2">
        <form @submit.prevent="saveCompany">
          <div class="row gy-3">
            <!-- RUC y Tipo de Empresa -->
            <div class="col-md-6">
              <label class="form-label small mb-1">RUC <span class="text-danger">*</span></label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.ruc"
                     placeholder="Ej: 0391034081001 (13 dígitos)" maxlength="13" :disabled="loadingRucCheck" 
                     @input="validateRucWithBackend"/>
              <small class="form-text" :class="{'text-danger': !isValidRuc || hasRucWarning, 'text-success': isValidRuc && !hasRucWarning}" v-if="rucValidationMessage">
                {{ rucValidationMessage }}
              </small>
            </div>
            <div class="col-md-6">
              <label class="form-label small mb-1">Tipo de Empresa <span class="text-danger">*</span></label>
              <select class="form-select form-select-sm" v-model="company.company_type">
                <option value="natural">Natural</option>
                <option value="juridica">Juridica</option>
                <option value="publico">Público</option>
              </select>
            </div>

            <!-- Razón Social y Nombre Comercial -->
            <div class="col-md-6">
              <label class="form-label small mb-1">
                Razón Social <span class="text-danger">*</span>
                <span v-if="autoFilledFields.business_name" class="badge bg-success ms-1" title="Información cargada desde el SRI">
                  ✓ SRI
                </span>
              </label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.business_name"
                     :class="{'border-success': autoFilledFields.business_name}"
                     placeholder="Nombre de la persona o empresa" :disabled="isEditMode" />
            </div>
            <div class="col-md-6">
              <label class="form-label small mb-1">
                Nombre Comercial
                <span v-if="autoFilledFields.trade_name" class="badge bg-success ms-1" title="Información cargada desde el SRI">
                  ✓ SRI
                </span>
              </label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.trade_name"
                     :class="{'border-success': autoFilledFields.trade_name}"
                     placeholder="Nombre comercial si es diferente a la razón social" />
              <small class="form-text text-muted">Opcional: Solo si es diferente a la razón social</small>
            </div>

            <!-- Dirección, Provincia y Ciudad -->
            <div class="col-md-12">
              <label class="form-label small mb-1">
                Dirección <span class="text-danger">*</span>
                <span v-if="autoFilledFields.address" class="badge bg-success ms-1" title="Información cargada desde el SRI">
                  ✓ SRI
                </span>
              </label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.address"
                     :class="{'border-success': autoFilledFields.address}"
                     placeholder="Dirección completa" required />
            </div>
            <div class="col-md-6">
              <label class="form-label small mb-1">
                Provincia <span class="text-danger">*</span>
                <span v-if="autoFilledFields.province" class="badge bg-success ms-1" title="Información cargada desde el SRI">
                  ✓ SRI
                </span>
              </label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.province"
                     :class="{'border-success': autoFilledFields.province}"
                     placeholder="Provincia" required />
            </div>
            <div class="col-md-6">
              <label class="form-label small mb-1">
                Ciudad <span class="text-danger">*</span>
                <span v-if="autoFilledFields.city" class="badge bg-success ms-1" title="Información cargada desde el SRI">
                  ✓ SRI
                </span>
              </label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.city"
                     :class="{'border-success': autoFilledFields.city}"
                     placeholder="Ciudad donde opera la empresa" required />
            </div>

            <!-- Teléfono y WhatsApp -->
            <div class="col-md-6">
              <label class="form-label small mb-1">Teléfono</label>
              <input type="tel" class="form-control form-control-sm" v-model.trim="company.phone"
                     placeholder="Ej: 022345678" />
              <small class="form-text text-muted">Opcional: Teléfono fijo de la empresa</small>
            </div>
            <div class="col-md-6">
              <label class="form-label small mb-1">WhatsApp <span class="text-danger">*</span></label>
              <div class="input-group input-group-sm">
                <span class="input-group-text">+593</span>
                <input type="tel" class="form-control form-control-sm" v-model.trim="company.whatsapp_number"
                       placeholder="Ej: 987654321" maxlength="9" @input="validateWhatsAppNumber"/>
              </div>
              <small class="form-text text-muted">Número de WhatsApp de la empresa</small>
            </div>

            <!-- Correo Electrónico -->
            <div class="col-md-12">
              <label class="form-label small mb-1">
                Correo Electrónico <span class="text-danger">*</span>
                <span v-if="autoFilledFields.email" class="badge bg-success ms-1" title="Información cargada desde el SRI">
                  ✓ SRI
                </span>
              </label>
              <input type="email" class="form-control form-control-sm" v-model.trim="company.email"
                     :class="{'border-success': autoFilledFields.email}"
                     placeholder="correo@empresa.com" />
            </div>

            <!-- Representante Legal -->
            <div class="col-12">
              <h6 class="mt-3 mb-3">Datos del Representante Legal</h6>
            </div>

            <div class="col-md-6">
              <label class="form-label small mb-1">Tipo de Identificación <span class="text-danger">*</span></label>
              <select class="form-select form-select-sm" v-model="company.legal_representative_id_type">
                <option v-for="type in idTypes" :key="type.id" :value="type.codigo">
                  {{ type.tipo_de_identificacion }}
                </option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label small mb-1">
                Identificación <span class="text-danger">*</span>
                <span v-if="autoFilledFields.legal_representative_id" class="badge bg-info ms-1" title="Auto-llenado para persona natural">
                  ✓ Auto
                </span>
              </label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.legal_representative_id"
                     :class="{'border-info': autoFilledFields.legal_representative_id}"
                     placeholder="Número de identificación" />
            </div>

            <div class="col-md-12">
              <label class="form-label small mb-1">
                Nombre Completo <span class="text-danger">*</span>
                <span v-if="autoFilledFields.legal_representative" class="badge bg-info ms-1" title="Auto-llenado para persona natural">
                  ✓ Auto
                </span>
              </label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.legal_representative"
                     :class="{'border-info': autoFilledFields.legal_representative}"
                     placeholder="Nombres y Apellidos del Representante Legal" />
            </div>

            <div class="col-md-12">
              <label class="form-label small mb-1">
                Dirección <span class="text-danger">*</span>
                <span v-if="autoFilledFields.legal_representative_address" class="badge bg-info ms-1" title="Auto-llenado para persona natural">
                  ✓ Auto
                </span>
              </label>
              <input type="text" class="form-control form-control-sm" v-model.trim="company.legal_representative_address"
                     :class="{'border-info': autoFilledFields.legal_representative_address}"
                     placeholder="Dirección del Representante Legal" />
            </div>

            <div class="col-md-6">
              <label class="form-label small mb-1">
                Correo Electrónico <span class="text-danger">*</span>
                <span v-if="autoFilledFields.legal_representative_email" class="badge bg-info ms-1" title="Auto-llenado para persona natural">
                  ✓ Auto
                </span>
              </label>
              <input type="email" class="form-control form-control-sm" v-model.trim="company.legal_representative_email"
                     :class="{'border-info': autoFilledFields.legal_representative_email}"
                     placeholder="correo@representante.com" />
            </div>

            <div class="col-md-6">
              <label class="form-label small mb-1">WhatsApp <span class="text-danger">*</span></label>
              <div class="input-group input-group-sm">
                <span class="input-group-text">+593</span>
                <input type="tel" class="form-control form-control-sm" v-model.trim="company.legal_representative_phone_number"
                       placeholder="Ej: 987654321" maxlength="9" @input="validateLegalRepPhoneNumber"/>
              </div>
              <small class="form-text text-muted">Ingrese el número sin el 0 inicial</small>
            </div>

            <div class="col-12 text-end mt-4">
              <button type="submit" class="btn btn-primary btn-lg px-5" :disabled="(!isValidRuc && !isEditMode) || savingCompany || loadingRucCheck">
                <span v-if="savingCompany" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>{{ isEditMode ? 'Actualizar Empresa' : 'Guardar Empresa' }}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue'
import { notifySuccess, notifyError, notifyInfo, notifyWarning } from '@/services/notificationService'
import { useRoute } from 'vue-router'
import api from '@/services/api'

export default {
  name: 'EmpresasCrearAdmin', 
  setup() {
    const route = useRoute()

    const company = ref({
      id: null,
      ruc: '',
      business_name: '',
      trade_name: '',
      company_type: 'natural',
      address: '',
      province: '',
      city: '',
      phone: '',
      whatsapp: '+593',
      whatsapp_number: '',
      email: '',
      legal_representative_id_type: '5', // Cédula por defecto
      legal_representative_id: '',
      legal_representative: '',
      legal_representative_address: '',
      legal_representative_email: '',
      legal_representative_phone: '+593',
      legal_representative_phone_number: '',
    })

    const isEditMode = ref(false)
    const loadingRucCheck = ref(false)
    const savingCompany = ref(false)
    const isValidRuc = ref(false)
    const rucValidationMessage = ref('') // Inicializar vacÃ­o para no mostrar mensaje al cargar
    const hasRucWarning = ref(false)
    const validationTimeout = ref(null)
    const autoFilledFields = ref({
      business_name: false,
      trade_name: false,
      email: false,
      address: false,
      province: false,
      city: false,
      legal_representative: false,
      legal_representative_email: false,
      legal_representative_id: false,
      legal_representative_address: false
    })
    const idTypes = ref([]) // Para almacenar los tipos de identificación

    const validateRucWithBackend = async () => {
      const ruc = company.value.ruc
      
      // Clear previous timeout
      if (validationTimeout.value) {
        clearTimeout(validationTimeout.value)
      }

      // Si el campo estÃ¡ vacÃ­o, no mostrar mensaje
      if (ruc.length === 0) {
        isValidRuc.value = false
        hasRucWarning.value = false
        rucValidationMessage.value = ''
        return
      }

      // Basic length validation - solo mostrar error si ya hay contenido
      if (ruc.length > 0 && ruc.length !== 13) {
        isValidRuc.value = false
        hasRucWarning.value = false
        rucValidationMessage.value = 'El RUC debe tener 13 dígitos'
        return
      }

      // Basic format validation - solo si hay contenido
      if (ruc.length > 0 && !/^\d+$/.test(ruc)) {
        isValidRuc.value = false
        hasRucWarning.value = false
        rucValidationMessage.value = 'El RUC debe contener solo números'
        return
      }

      // Set loading state
      loadingRucCheck.value = true
      rucValidationMessage.value = 'Validando RUC...'

      // Debounce the API call
      validationTimeout.value = setTimeout(async () => {
        try {
          const response = await api.get(`/sri/validate-ruc/${ruc}`)
          
          // Verificar que la respuesta tenga la estructura esperada
          if (!response || !response.data) {
            throw new Error('Respuesta inválida del servidor')
          }
          
          if (response.data.valid) {
            isValidRuc.value = true
            
            // Auto-set company type based on RUC type
            if (response.data.type === 'natural') {
              company.value.company_type = 'natural'
            } else if (response.data.type === 'public') {
              company.value.company_type = 'publico'
            } else {
              company.value.company_type = 'juridica'
            }

            console.log('Response data:', response.data)

            // Check if catastro data is available and pre-fill form
            console.log('Catastro found:', response.data.catastro_found)
            console.log('Catastro data:', response.data.catastro_data)
            if (response.data.catastro_found && response.data.catastro_data) {
              const catastro = response.data.catastro_data
              
              // Reset auto-filled fields tracking
              autoFilledFields.value = {
                business_name: false,
                trade_name: false,
                email: false,
                address: false,
                province: false,
                city: false,
                legal_representative: false,
                legal_representative_email: false,
                legal_representative_id: false,
                legal_representative_address: false
              }
              
              // Pre-fill form with catastro data (with null checks)
              if (catastro.razon_social && catastro.razon_social.trim() && !company.value.business_name) {
                company.value.business_name = catastro.razon_social.trim()
                autoFilledFields.value.business_name = true
              }
              
              // Para nombre comercial: solo llenar si es diferente a la razón social
              if (catastro.nombre_comercial && catastro.nombre_comercial.trim() && !company.value.trade_name) {
                const nombreComercial = catastro.nombre_comercial.trim()
                const razonSocial = catastro.razon_social ? catastro.razon_social.trim() : ''
                
                // Solo llenar si el nombre comercial es diferente a la razón social
                if (nombreComercial !== razonSocial) {
                  company.value.trade_name = nombreComercial
                  autoFilledFields.value.trade_name = true
                }
              }
              
              if (catastro.correo_electronico && catastro.correo_electronico.trim() && !company.value.email) {
                company.value.email = catastro.correo_electronico.trim()
                autoFilledFields.value.email = true
              }
              
              if (catastro.direccion_completa && catastro.direccion_completa.trim() && !company.value.address) {
                company.value.address = catastro.direccion_completa.trim()
                autoFilledFields.value.address = true
              }
              
              if (catastro.canton && catastro.canton.trim() && !company.value.city) {
                company.value.city = catastro.canton.trim()
                autoFilledFields.value.city = true
              }

              if (catastro.provincia && catastro.provincia.trim() && !company.value.province) {
                company.value.province = catastro.provincia.trim()
                autoFilledFields.value.province = true
              }

              // Para personas naturales, auto-llenar datos del representante legal
              if (response.data.type === 'natural' && catastro.razon_social && catastro.razon_social.trim()) {
                const razonSocial = catastro.razon_social.trim()
                
                // Auto-llenar representante legal con la razón social (nombre de la persona)
                if (!company.value.legal_representative) {
                  company.value.legal_representative = razonSocial
                  autoFilledFields.value.legal_representative = true
                }

                // Auto-llenar identificación: RUC sin los últimos 3 dígitos (001)
                if (!company.value.legal_representative_id && ruc.length === 13) {
                  company.value.legal_representative_id = ruc.substring(0, 10) // Quita los últimos 3 dígitos
                  company.value.legal_representative_id_type = '5' // CÉDULA (código según catálogo)
                  autoFilledFields.value.legal_representative_id = true
                }

                // Auto-llenar dirección del representante con la dirección de la empresa
                if (!company.value.legal_representative_address && catastro.direccion_completa) {
                  company.value.legal_representative_address = catastro.direccion_completa
                  autoFilledFields.value.legal_representative_address = true
                }
                
                // Auto-llenar email del representante legal si está disponible y no hay uno ya
                if (catastro.correo_electronico && catastro.correo_electronico.trim() && !company.value.legal_representative_email) {
                  company.value.legal_representative_email = catastro.correo_electronico.trim()
                  autoFilledFields.value.legal_representative_email = true
                }
              }

              rucValidationMessage.value = `RUC válido (${response.data.description}) - Datos del SRI cargados`
              notifySuccess('Información del SRI cargada automáticamente')
            } else {
              // Reset auto-filled fields if no catastro data
              autoFilledFields.value = {
                business_name: false,
                trade_name: false,
                email: false,
                address: false,
                province: false,
                city: false,
                legal_representative: false,
                legal_representative_email: false
              }

              // Mostrar mensaje específico si hay error de catastro
              if (response.data.catastro_error) {
                hasRucWarning.value = false // RUC válido, mostrar en verde
                rucValidationMessage.value = `RUC válido (${response.data.description})`
                // Mostrar error en notificación del sistema
                console.log('Mostrando error:', response.data.catastro_error)
                notifyError(response.data.catastro_error)
              } else {
                hasRucWarning.value = false
                rucValidationMessage.value = `RUC válido (${response.data.description}) - No encontrado en catastro SRI`
                notifyInfo('RUC válido, pero no se encontró información adicional en el catastro del SRI')
              }
            }

            // After successful validation, check if RUC already exists in our system
            await checkRucExistence(ruc)
          } else {
            isValidRuc.value = false
            hasRucWarning.value = false
            rucValidationMessage.value = 'RUC invalido según algoritmo del SRI'
            console.log('Response data (invalid):', response.data)
          }
        } catch (error) {
          console.error('Error al validar RUC:', error)
          isValidRuc.value = false
          hasRucWarning.value = false
          
          // Reset auto-filled fields on error
          autoFilledFields.value = {
            business_name: false,
            trade_name: false,
            email: false,
            address: false,
            province: false,
            city: false,
            legal_representative: false,
            legal_representative_email: false
          }
          
          // Manejo más específico de errores
          if (error.response) {
            const status = error.response.status
            const data = error.response.data
            
            if (status === 404) {
              rucValidationMessage.value = 'Servicio de validación SRI no disponible'
              notifyWarning('Servicio de validación del SRI temporalmente no disponible')
            } else if (status === 400) {
              if (data && data.error) {
                rucValidationMessage.value = data.error
              } else {
                rucValidationMessage.value = 'El formato del RUC no es válido'
              }
            } else if (status === 500) {
              rucValidationMessage.value = 'Error interno del servidor al validar RUC'
              notifyError('Error interno del servidor. Intente más tarde.')
            } else {
              rucValidationMessage.value = data && data.message ? data.message : 'Error al validar RUC'
            }
          } else if (error.request) {
            rucValidationMessage.value = 'No se pudo conectar al servidor'
            notifyError('No se pudo conectar al servidor de validación')
          } else {
            rucValidationMessage.value = 'Error al validar RUC. Intente nuevamente.'
            notifyError('Error inesperado al validar RUC')
          }
        } finally {
          loadingRucCheck.value = false
        }
      }, 800) // Wait 800ms after user stops typing
    }

    const validateRuc = () => {
      // Legacy function - now just calls the backend version
      validateRucWithBackend()
    }

    const validateWhatsAppNumber = () => {
      let number = company.value.whatsapp_number
      
      // Remove any non-digit characters
      number = number.replace(/\D/g, '')
      
      // Remove leading zero if present
      if (number.startsWith('0')) {
        number = number.substring(1)
      }
      
      // Update the model
      company.value.whatsapp_number = number
      
      // Update the complete whatsapp field for backend
      company.value.whatsapp = number ? `+593${number}` : '+593'
    }

    const validateLegalRepPhoneNumber = () => {
      let number = company.value.legal_representative_phone_number
      
      // Remove any non-digit characters
      number = number.replace(/\D/g, '')
      
      // Remove leading zero if present
      if (number.startsWith('0')) {
        number = number.substring(1)
      }
      
      // Update the model
      company.value.legal_representative_phone_number = number
      
      // Update the complete phone field for backend
      company.value.legal_representative_phone = number ? `+593${number}` : '+593'
    }

    // Validador de cédula (Ecuador) en JS - implementa algoritmo de verificación
    const esCedulaValidaJS = (cedula) => {
      if (!cedula || typeof cedula !== 'string') return false
      const num = cedula.replace(/\D/g, '')
      if (!/^\d{10}$/.test(num)) return false

      const provincia = parseInt(num.substring(0,2), 10)
      if (!((provincia >= 1 && provincia <= 24) || provincia === 30)) return false

      const digitos = num.split('').map(d => parseInt(d, 10))
      let suma = 0
      for (let i = 0; i < 9; i++) {
        let valor = digitos[i]
        if (i % 2 === 0) {
          valor = valor * 2
          if (valor > 9) valor -= 9
        }
        suma += valor
      }
      const modulo = suma % 10
      const digitoVerificador = modulo === 0 ? 0 : 10 - modulo
      return digitoVerificador === digitos[9]
    }

    // Detectar automáticamente tipo de identificación al ingresar identificación del representante
    watch(() => company.value.legal_representative_id, (newVal) => {
      const id = (newVal || '').toString().trim()
      if (id.length === 0) return

      // Si parece cédula, validar y seleccionar el tipo correspondiente en el dropdown
      if (/^\d{10}$/.test(id) && esCedulaValidaJS(id)) {
        // Buscar en idTypes el código cuyo texto contenga 'Cédula' (case-insensitive)
        const cedulaType = idTypes.value.find(t => /cedula/i.test(t.tipo_de_identificacion))
        if (cedulaType) {
          company.value.legal_representative_id_type = cedulaType.codigo.toString()
        } else {
          // Fallback: si no hay catálogo, usar '5' (CÉDULA) como convención del backend
          company.value.legal_representative_id_type = '5'
        }
      }
    })

    const checkRucExistence = async (ruc) => {
      try {
        const response = await api.get(`/registro-empresas/verificar-ruc/${ruc}`)
        
        if (response.data.exists) {
          const existingCompany = response.data.company
          
          // Split phone numbers from the complete format
          const whatsappMatch = existingCompany.whatsapp ? existingCompany.whatsapp.match(/\+593(.+)/) : null
          const legalPhoneMatch = existingCompany.legal_representative_phone ? existingCompany.legal_representative_phone.match(/\+593(.+)/) : null
          
          // Mapear campos del backend (espaÃ±ol) al frontend (inglÃ©s)
          company.value = { 
            id: existingCompany.id,
            ruc: existingCompany.ruc,
            business_name: existingCompany.razon_social || '', // Backend usa 'razon_social'
            trade_name: existingCompany.trade_name || '',
            company_type: existingCompany.company_type || 'natural',
            address: existingCompany.direccion || '', // Backend usa 'direccion'
            province: existingCompany.province || existingCompany.provincia || '',
            city: existingCompany.ciudad || '', // Backend usa 'ciudad'
            phone: existingCompany.telefono || '', // Backend usa 'telefono'
            whatsapp: existingCompany.whatsapp || '+593',
            whatsapp_number: whatsappMatch ? whatsappMatch[1] : '',
            email: existingCompany.email || '',
            legal_representative_id_type: existingCompany.tipo_identificacion || '5',
            legal_representative_id: existingCompany.identificacion_representante || '',
            legal_representative: existingCompany.legal_representative || '',
            legal_representative_address: existingCompany.direccion_representante || '',
            legal_representative_email: existingCompany.legal_representative_email || '',
            legal_representative_phone: existingCompany.legal_representative_phone || '+593',
            legal_representative_phone_number: legalPhoneMatch ? legalPhoneMatch[1] : ''
          }
          
          // Reset auto-filled fields ya que estos datos vienen de la BD, no del SRI
          autoFilledFields.value = {
            business_name: false,
            trade_name: false,
            email: false,
            address: false,
            province: false,
            city: false,
            legal_representative: false,
            legal_representative_email: false,
            legal_representative_id: false,
            legal_representative_address: false
          }
          
          isEditMode.value = true
          notifyInfo('Empresa encontrada. Editando información existente.')
        }
      } catch (error) {
        console.error('Error al verificar RUC:', error)
        
        // Solo mostrar error si no es un 404 (RUC no encontrado es normal)
        // y no es un error de validación esperado
        if (error.response) {
          const status = error.response.status
          const data = error.response.data
          
          if (status === 404) {
            // 404 es normal - RUC no existe en nuestro sistema, no mostrar error
            return
          } else if (status === 400) {
            // Error de validación del RUC - no mostrar toast, ya se maneja en la UI
            return
          } else if (status === 500) {
            notifyError('Error interno del servidor al verificar RUC registrado')
          } else {
            if (data && data.message) {
              notifyError(data.message)
            } else {
              notifyError('Error al verificar si el RUC ya está registrado')
            }
          }
        } else {
          // Error de red o conexión
          notifyError('No se pudo conectar al servidor para verificar el RUC')
        }
      }
    }

    const resetFormExceptRuc = () => {
      const ruc = company.value.ruc
      company.value = {
        id: null,
        ruc,
        business_name: '',
        trade_name: '',
        company_type: 'natural',
        address: '',
        province: '',
        city: '',
        phone: '',
        whatsapp: '+593',
        whatsapp_number: '',
        email: '',
        legal_representative_id_type: '5',
        legal_representative_id: '',
        legal_representative: '',
        legal_representative_address: '',
        legal_representative_email: '',
        legal_representative_phone: '+593',
        legal_representative_phone_number: '',
      }
      
      // Reset auto-filled fields tracking
      autoFilledFields.value = {
        business_name: false,
        trade_name: false,
        email: false,
        address: false,
        province: false,
        city: false,
        legal_representative: false,
        legal_representative_email: false,
        legal_representative_id: false,
        legal_representative_address: false
      }
      
      isValidRuc.value = false
      rucValidationMessage.value = '' // No mostrar mensaje hasta que sea necesario
    }

    // FunciÃ³n de validaciÃ³n personalizada
    const validateCompanyForm = () => {
      const errors = []

      // Validar RUC
      if (!company.value.ruc || company.value.ruc.trim() === '') {
        errors.push('El RUC es obligatorio')
      } else if (company.value.ruc.length !== 13) {
        errors.push('El RUC debe tener exactamente 13 dÃ­gitos')
      }

      // Validar tipo de empresa
      if (!company.value.company_type || company.value.company_type.trim() === '') {
        errors.push('El tipo de empresa es obligatorio')
      }

      // Validar razÃ³n social
      if (!company.value.business_name || company.value.business_name.trim() === '') {
        errors.push('La razón social es obligatoria')
      }

      // Validar WhatsApp de la empresa
      if (!company.value.whatsapp_number || company.value.whatsapp_number.trim() === '') {
        errors.push('El número de WhatsApp de la empresa es obligatorio')
      } else if (company.value.whatsapp_number.length !== 9) {
        errors.push('El número de WhatsApp debe tener 9 dígitos')
      }

      // Validar correo electrónico
      if (!company.value.email || company.value.email.trim() === '') {
        errors.push('El correo electrónico es obligatorio')
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(company.value.email)) {
        errors.push('El correo electrónico no tiene un formato válido')
      }

      // Validar representante legal
      if (!company.value.legal_representative || company.value.legal_representative.trim() === '') {
        errors.push('El nombre del representante legal es obligatorio')
      }

      // Validar correo electrónico del representante legal
      if (!company.value.legal_representative_email || company.value.legal_representative_email.trim() === '') {
        errors.push('El correo electrónico del representante legal es obligatorio')
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(company.value.legal_representative_email)) {
        errors.push('El correo electrónico del representante legal no tiene un formato válido')
      }

      // Validar tipo de identificación del representante
      if (!company.value.legal_representative_id_type || company.value.legal_representative_id_type === '') {
        errors.push('El tipo de identificación del representante legal es obligatorio')
      }

      // Validar identificación del representante
      if (!company.value.legal_representative_id || company.value.legal_representative_id.trim() === '') {
        errors.push('La identificación del representante legal es obligatoria')
      }

      // Validar dirección del representante
      if (!company.value.legal_representative_address || company.value.legal_representative_address.trim() === '') {
        errors.push('La dirección del representante legal es obligatoria')
      }

      return errors
    }

    const saveCompany = async () => {
      if (!isValidRuc.value) return

      // Validación personalizada con mensajes centrados
      const validationErrors = validateCompanyForm()
      if (validationErrors.length > 0) {
        // Mostrar el primer error con toast centrado
        notifyError(validationErrors[0])
        return
      }

      savingCompany.value = true
      try {
        // Preparar datos para envío - mapear campos al formato esperado por el backend
        const companyData = {
          ruc: company.value.ruc,
          razon_social: company.value.business_name, // Mapear business_name a razon_social
          direccion: company.value.address, // Mapear address a direccion
          telefono: company.value.phone || '', // Mapear phone a telefono (siempre enviar cadena)
          whatsapp: company.value.whatsapp, // WhatsApp de la empresa
          email: company.value.email,
          legal_representative: company.value.legal_representative,
          legal_representative_email: company.value.legal_representative_email,
          legal_representative_phone: company.value.legal_representative_phone,
          // Nuevos campos del representante legal
          tipo_identificacion: company.value.legal_representative_id_type,
          identificacion_representante: company.value.legal_representative_id,
          direccion_representante: company.value.legal_representative_address,
          // Campos opcionales
          trade_name: company.value.trade_name,
          company_type: company.value.company_type,
          ciudad: company.value.city,
          provincia: company.value.province,
          subdomain: company.value.subdomain,
        }
        
        // Si no hay nombre comercial, usar la razón social
        if (!companyData.trade_name || companyData.trade_name.trim() === '') {
          companyData.trade_name = companyData.razon_social
        }
        
        console.log('Datos a enviar al backend:', companyData)
        
        // Rutas canónicas en español según SYSTEM.md
        const endpoint = isEditMode.value ? `/registro-empresas/${company.value.id}` : '/registro-empresas'
        const method = isEditMode.value ? 'put' : 'post'
        
        await api[method](endpoint, companyData)
        
        notifySuccess(
          isEditMode.value ? 'Empresa actualizada con éxito' : 'Empresa creada con éxito'
        )
        
        // Limpiar el formulario después de guardar exitosamente
        if (!isEditMode.value) {
          // Si era una nueva empresa, limpiar todo el formulario
          company.value = {
            id: null,
            ruc: '',
            business_name: '',
            trade_name: '',
            company_type: 'natural',
            address: '',
            province: '',
            city: '',
            phone: '',
            whatsapp: '+593',
            whatsapp_number: '',
            email: '',
            legal_representative_id_type: '5',
            legal_representative_id: '',
            legal_representative: '',
            legal_representative_address: '',
            legal_representative_email: '',
            legal_representative_phone: '+593',
            legal_representative_phone_number: '',
          }
          
          // Reset validation state
          isValidRuc.value = false
          rucValidationMessage.value = '' // Limpiar mensaje después de guardar
          isEditMode.value = false
          
          // Reset auto-filled fields
          autoFilledFields.value = {
            business_name: false,
            trade_name: false,
            email: false,
            address: false,
            province: false,
            city: false,
            legal_representative: false,
            legal_representative_email: false,
            legal_representative_id: false,
            legal_representative_address: false
          }
        }
      } catch (error) {
        console.error('Error al guardar empresa:', error)
        
        // Mostrar mensaje de error más específico
        let errorMessage = 'Error al guardar la empresa'
        if (error.response && error.response.data) {
          if (error.response.data.error) {
            errorMessage = error.response.data.error
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          }
        }
        
        notifyError(errorMessage)
      } finally {
        savingCompany.value = false
      }
    }

    // Watch for RUC changes
    watch(() => company.value.ruc, (newRuc, oldRuc) => {
      // Solo resetear si no estamos en modo edición o si es un cambio manual del usuario
      if (newRuc !== oldRuc && !isEditMode.value) {
        isValidRuc.value = false
        rucValidationMessage.value = '' // No mostrar mensaje hasta que sea necesario
        
        // Reset auto-filled fields when RUC changes
        autoFilledFields.value = {
          business_name: false,
          trade_name: false,
          email: false,
          address: false,
          city: false,
          legal_representative: false,
          legal_representative_email: false
        }
      }
      
      // Reset form when RUC is incomplete (solo si no está en modo edición)
      if (newRuc.length < 13 && !isEditMode.value) {
        resetFormExceptRuc()
      }
    })

    // Función para cargar empresa por ID (para modo edición)
    const loadCompanyForEdit = async (id) => {
      try {
        loadingRucCheck.value = true
        rucValidationMessage.value = 'Cargando datos de la empresa...'
        
        const response = await api.get(`/registro-empresas/subdominios`)
        
        if (response.data.success) {
          const empresas = response.data.data
          const empresa = empresas.find(emp => emp.id == id)
          
          if (empresa) {
            // Separar números de teléfono del formato completo
            const whatsappMatch = empresa.whatsapp ? empresa.whatsapp.match(/\+593(.+)/) : null
            const legalPhoneMatch = empresa.legal_representative_phone ? empresa.legal_representative_phone.match(/\+593(.+)/) : null
            
            // Mapear campos del backend al frontend
            company.value = { 
              id: empresa.id,
              ruc: empresa.ruc,
              business_name: empresa.razon_social || '',
              trade_name: empresa.trade_name || '',
              company_type: empresa.company_type || 'natural',
              address: empresa.direccion || '',
              province: empresa.province || empresa.provincia || '',
              city: empresa.ciudad || '',
              phone: empresa.telefono || '',
              whatsapp: empresa.whatsapp || '+593',
              whatsapp_number: whatsappMatch ? whatsappMatch[1] : '',
              email: empresa.email || '',
              legal_representative: empresa.legal_representative || '',
              legal_representative_email: empresa.legal_representative_email || '',
              legal_representative_phone: empresa.legal_representative_phone || '+593',
              legal_representative_phone_number: legalPhoneMatch ? legalPhoneMatch[1] : ''
            }
            
            // Reset auto-filled fields ya que estos datos vienen de la BD
            autoFilledFields.value = {
              business_name: false,
              trade_name: false,
              email: false,
              address: false,
              city: false,
              legal_representative: false,
              legal_representative_email: false
            }
            
            isEditMode.value = true
            isValidRuc.value = true
            rucValidationMessage.value = 'RUC válido'
            notifySuccess('Empresa cargada para edición')
          } else {
            notifyError('Empresa no encontrada')
          }
        }
      } catch (error) {
        console.error('Error al cargar empresa:', error)
        notifyError('Error al cargar los datos de la empresa')
      } finally {
        loadingRucCheck.value = false
      }
    }

    const loadIdTypes = async () => {
      try {
        const response = await api.get('/tipos-identificacion-sri')
        if (response.data.success) {
          idTypes.value = response.data.data
        } else {
          console.error('Error al cargar tipos de identificación:', response.data.message)
        }
      } catch (error) {
        console.error('Error al cargar tipos de identificación:', error)
      }
    }

    // Verificar si estamos en modo edición al montar el componente
    onMounted(async () => {
      // Cargar tipos de identificación
      await loadIdTypes()
      
      const empresaId = route.params.id
      if (empresaId) {
        loadCompanyForEdit(empresaId)
      }
    })

    return {
      company,
      isEditMode,
      loadingRucCheck,
      savingCompany,
      isValidRuc,
      rucValidationMessage,
      autoFilledFields,
      idTypes,
      validateRuc,
      validateRucWithBackend,
      validateWhatsAppNumber,
      validateLegalRepPhoneNumber,
      saveCompany
    }
  }
}
</script>

<style>
/* Contenedor principal */
.empresas-crear-container {
  background-color: var(--secondary-bg-main);
  color: var(--text-primary);
  width: 100%;
  max-width: none;
  margin: 0;
  box-sizing: border-box;
  overflow-x: hidden;
}

.page-title {
  color: var(--primary-main);
}

/* Tarjetas */
.card {
  background-color: var(--secondary-bg-main);
  border-color: var(--border-main);
}

.card-header {
  background-color: var(--primary-main);
  color: var(--text-inverse);
  border-color: var(--border-main);
  font-size: 1rem;
}

.card-body {
  background-color: var(--secondary-bg-main);
  color: var(--text-primary);
}

/* Formularios */
.form-label {
  font-weight: 500;
  color: var(--text-primary);
}

.form-control, .form-select {
  background-color: var(--input-bg);
  border-color: var(--border-main);
  color: var(--text-primary);
}

.form-control:focus, .form-select:focus {
  background-color: var(--input-bg);
  border-color: var(--primary-main);
  color: var(--text-primary);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.form-control:disabled {
  background-color: var(--secondary-bg-soft);
  color: var(--text-secondary);
  border-color: var(--border-main);
}

/* Estilos para placeholders */
.form-control::placeholder, .form-select::placeholder {
  color: var(--input-placeholder);
  opacity: 1;
}

.form-control::-webkit-input-placeholder {
  color: var(--input-placeholder);
  opacity: 1;
}

.form-control::-moz-placeholder {
  color: var(--input-placeholder);
  opacity: 1;
}

.form-control:-ms-input-placeholder {
  color: var(--input-placeholder);
  opacity: 1;
}

/* Estilos específicos para input groups */
.input-group .form-control::placeholder {
  color: var(--input-placeholder);
  opacity: 1;
}

/* Texto de ayuda */
.form-text {
  color: var(--text-secondary);
}

.text-danger {
  color: var(--accent-danger) !important;
}

.text-success {
  color: var(--accent-success) !important;
}

/* Estilos para campos auto-llenados desde el SRI */
.border-success {
  border-color: var(--accent-success) !important;
}

/* Estilos para campos auto-llenados para representante legal */
.border-info {
  border-color: var(--accent-info) !important;
}

.badge {
  font-size: 0.65rem;
}

/* Estilos para input group de teléfonos */
.input-group-text {
  background-color: var(--input-bg);
  border-color: var(--border-main);
  font-weight: 500;
  color: var(--text-primary);
}

.input-group .form-control {
  border-left: 0;
  background-color: var(--input-bg);
  border-color: var(--border-main);
  color: var(--text-primary);
}

.input-group .form-control:focus {
  border-color: var(--primary-main);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
  background-color: var(--input-bg);
}

/* Botones */
.btn-primary {
  background-color: var(--primary-main);
  border-color: var(--primary-main);
  color: var(--text-inverse);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-hover);
  border-color: var(--primary-hover);
}

.btn-primary:disabled {
  background-color: var(--secondary-bg-soft);
  border-color: var(--border-main);
  color: var(--text-secondary);
}
</style>


